// Cloudflare Turnstile (invisible mode) on-demand client.
//
// Why "on-demand mount-unmount":
//   iOS Safari's AutoFill / Keychain composite layer collides with the
//   Turnstile iframe — after a submit + scroll the bottom half of the
//   viewport renders as a dark unscrollable overlay. Keeping the iframe out
//   of the DOM except during the actual submit roundtrip sidesteps this.
//
// The script is warmed when /login mounts, but the widget itself is still
// created only during submit. This avoids making the first click pay the
// script download/initialization cost while keeping the iframe short-lived.

const API_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

type TurnstileApi = {
  ready: (callback: () => void) => void;
  render: (
    container: string | HTMLElement,
    params: {
      sitekey: string;
      action?: string;
      callback: (token: string) => void;
      retry?: "auto" | "never";
      "retry-interval"?: number;
      "error-callback"?: (errorCode: string) => boolean | void;
      "expired-callback"?: () => void;
    },
  ) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("not_in_browser"));
      return;
    }

    let settled = false;
    let readyTimer: ReturnType<typeof setTimeout> | undefined;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (readyTimer !== undefined) clearTimeout(readyTimer);
      fn();
    };
    const fail = (error: Error) => finish(() => reject(error));
    const resolveWhenReady = () => {
      const t = window.turnstile;
      if (!t) {
        fail(new Error("turnstile_unavailable"));
        return;
      }
      try {
        t.ready(() => finish(resolve));
      } catch (e) {
        fail(e instanceof Error ? e : new Error("turnstile_unavailable"));
      }
    };
    readyTimer = setTimeout(
      () => fail(new Error("turnstile_script_load_failed")),
      10_000,
    );

    if (window.turnstile) {
      resolveWhenReady();
      return;
    }

    const s = document.createElement("script");
    s.src = API_SRC;
    // Cloudflare requires a non-async/non-defer script when using ready().
    // Dynamically inserted scripts default to async=true, so opt out before
    // appending it.
    s.async = false;
    s.onload = resolveWhenReady;
    s.onerror = () => {
      fail(new Error("turnstile_script_load_failed"));
    };
    document.head.appendChild(s);
  })
    .catch((e) => {
      // Allow retry on the next submit after a transient load/init failure.
      scriptPromise = null;
      throw e;
    });
  return scriptPromise;
}

/** Warm api.js while the user is entering their email address. */
export function prepareTurnstile(): Promise<void> {
  return loadScript();
}

export type TurnstileTokenOptions = {
  sitekey: string;
  action: string;
  /** Hard cap on the whole render → token → cleanup roundtrip. */
  timeoutMs?: number;
};

// Render an invisible widget into an offscreen container, get a token, then
// fully tear down (remove widget + drop container). Caller passes the token
// to the server in a header / hidden field and forgets the rest.
export async function obtainTurnstileToken(
  opts: TurnstileTokenOptions,
): Promise<string> {
  await loadScript();
  const t = window.turnstile;
  if (!t) throw new Error("turnstile_unavailable");

  const container = document.createElement("div");
  container.style.cssText =
    "position:absolute;width:0;height:0;left:-9999px;top:0;overflow:hidden;visibility:hidden;pointer-events:none;";
  document.body.appendChild(container);

  return new Promise<string>((resolve, reject) => {
    let widgetId: string | undefined;
    let lastErrorCode: string | undefined;
    let settled = false;
    const cleanup = () => {
      if (widgetId !== undefined) {
        try {
          t.remove(widgetId);
        } catch {
          /* widget already gone */
        }
        widgetId = undefined;
      }
      try {
        container.remove();
      } catch {
        /* container already detached */
      }
    };
    const timer = setTimeout(() => {
      settled = true;
      cleanup();
      reject(
        new Error(
          lastErrorCode
            ? `turnstile_error:${lastErrorCode}`
            : "turnstile_timeout",
        ),
      );
    }, opts.timeoutMs ?? 15_000);

    try {
      widgetId = t.render(container, {
        sitekey: opts.sitekey,
        action: opts.action,
        retry: "auto",
        "retry-interval": 2_000,
        callback: (token: string) => {
          settled = true;
          clearTimeout(timer);
          cleanup();
          resolve(token);
        },
        "error-callback": (errorCode: string) => {
          lastErrorCode = errorCode;
          // Returning false leaves Turnstile's automatic retry active. The
          // outer timeout remains the hard cap and performs cleanup if all
          // retries fail.
          return false;
        },
      });
    } catch (e) {
      settled = true;
      clearTimeout(timer);
      cleanup();
      reject(e instanceof Error ? e : new Error("turnstile_render_failed"));
      return;
    }
    // The success callback can fire synchronously inside render(), before
    // widgetId is assigned — in that case cleanup ran without removing the
    // widget, so finish the removal now that the id is known.
    if (settled) cleanup();
  });
}
